import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Mail, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_d8692872-ce9e-4544-878c-3a5b2f1a67b0/artifacts/lff9980v_GTIC%20STUDIO%20LOGO2.png";

export default function LoginPage() {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(email, password);
      toast.success(`Bienvenue, ${user.name} !`);
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Identifiants incorrects');
    } finally {
      setLoading(false);
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12 bg-[#F8F9FA]" data-testid="login-page">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
        className="w-full max-w-md"
      >
        <Card className="border-0 shadow-xl" data-testid="login-form">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <Link to="/">
                <img src={LOGO_URL} alt="GTIC SARL" className="h-14 mx-auto mb-4" />
              </Link>
              <h1 className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">
                Administration
              </h1>
              <p className="text-gray-600 mt-1">
                Connectez-vous à votre compte
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-500" />
                  {t('contact_email')}
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="admin@gticsarl.com"
                  className="mt-1"
                  data-testid="login-email"
                />
              </div>
              <div>
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="h-4 w-4 text-gray-500" />
                  Mot de passe
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="mt-1"
                  data-testid="login-password"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#0B6AB3] hover:bg-[#095c9e] text-white"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? 'Connexion...' : (
                  <>
                    {t('nav_login')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/" className="text-[#0B6AB3] text-sm hover:underline">
                ← Retour à l'accueil
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
